import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import { products, categories } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";

const FB_MARKETPLACE_URL = "https://www.facebook.com/marketplace/";

export default function ShopPage() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("popular");
  const [wishlist, setWishlist] = useState<number[]>([]);
  const [addedIds, setAddedIds] = useState<number[]>([]);
  const { addToCart } = useCart();

  const activeCategory = searchParams.get("category") || "all";

  const setCategory = (cat: string) => {
    if (cat === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== "all") {
      list = list.filter((p) => p.category === activeCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.nameF.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      );
    }
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "newest") list.sort((a, b) => b.id - a.id);
    return list;
  }, [activeCategory, search, sort]);

  const handleAddToCart = (product: typeof products[0]) => {
    addToCart({ id: product.id, name: product.name, nameF: product.nameF, price: product.price, image: product.image });
    setAddedIds((prev) => [...prev, product.id]);
    setTimeout(() => {
      setAddedIds((prev) => prev.filter((id) => id !== product.id));
      // Redirect to Facebook Marketplace after adding to cart
      window.open(FB_MARKETPLACE_URL, "_blank");
    }, 800);
  };

  const toggleWishlist = (id: number) => {
    setWishlist((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const badgeColors: Record<string, string> = {
    popular: "bg-amber-600",
    new: "bg-emerald-500",
    sale: "bg-amber-500",
  };

  return (
    <div className="min-h-screen bg-[#f5ede0]">
      <Navbar />

      {/* Hero Banner */}
      <div className="relative pt-20 pb-16 bg-gradient-to-br from-amber-900 via-stone-800 to-amber-800 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=beautiful%20gift%20shop%20display%20with%20flowers%20candles%20and%20gift%20boxes%20on%20elegant%20shelves%2C%20warm%20golden%20light%2C%20premium%20boutique%20interior%2C%20soft%20pink%20and%20cream%20tones&width=1920&height=400&seq=shop_hero&orientation=landscape"
            alt="Shop"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center py-10">
          <span className="inline-block text-amber-300 text-xs font-semibold uppercase tracking-widest mb-3">
            — Creative Hub —
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("shop_title")}
          </h1>
          <p className="text-white/70 text-lg">{t("shop_subtitle")}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        {/* Filters Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("shop_search")}
              className="w-full pl-10 pr-4 py-3 bg-white border border-amber-100 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-amber-300 transition-colors"
            />
          </div>
          {/* Sort */}
          <div className="relative">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 bg-white border border-amber-100 rounded-xl text-sm text-gray-700 focus:outline-none focus:border-amber-300 transition-colors cursor-pointer"
            >
              <option value="popular">{t("shop_sort_popular")}</option>
              <option value="price_asc">{t("shop_sort_price_asc")}</option>
              <option value="price_desc">{t("shop_sort_price_desc")}</option>
              <option value="newest">{t("shop_sort_newest")}</option>
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => setCategory("all")}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
              activeCategory === "all"
                ? "bg-gradient-to-r from-amber-700 to-amber-500 text-white"
                : "bg-white text-gray-600 border border-amber-100 hover:border-amber-300 hover:text-amber-700"
            }`}
          >
            {t("shop_filter_all")}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? "bg-gradient-to-r from-amber-700 to-amber-500 text-white"
                  : "bg-white text-gray-600 border border-amber-100 hover:border-amber-300 hover:text-amber-700"
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {isFr ? cat.labelF : cat.label}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-gray-600 mb-6">
          {filtered.length} {isFr ? "produits trouvés" : "products found"}
        </p>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-amber-50 mx-auto mb-4">
              <i className="ri-search-line text-4xl text-amber-300"></i>
            </div>
            <p className="text-gray-600">{t("shop_no_results")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl overflow-hidden border border-amber-100 hover:border-amber-200 hover:-translate-y-2 transition-all duration-400"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={product.image}
                    alt={isFr ? product.nameF : product.name}
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                  />
                  {product.badge && (
                    <span className={`absolute top-3 left-3 px-2.5 py-1 ${badgeColors[product.badge]} text-white text-xs font-bold rounded-full uppercase`}>
                      {t(`common_${product.badge}`)}
                    </span>
                  )}
                  <button
                    onClick={() => toggleWishlist(product.id)}
                    className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-all duration-200 cursor-pointer"
                  >
                    <i className={`${wishlist.includes(product.id) ? "ri-heart-fill text-amber-600" : "ri-heart-line text-gray-400"} text-base`}></i>
                  </button>
                </div>
                <div className="p-4">
                  <h3
                    className="font-bold text-gray-800 text-sm mb-1 group-hover:text-amber-700 transition-colors"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {isFr ? product.nameF : product.name}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">
                    {isFr ? product.descriptionF : product.description}
                  </p>
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className={`ri-star-fill text-xs ${j < Math.floor(product.rating) ? "text-amber-400" : "text-gray-200"}`}></i>
                    ))}
                    <span className="text-xs text-gray-400 ml-1">({product.reviews})</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <a
                        href={`https://wa.me/237678012159?text=Bonjour! Je voudrais commander: ${isFr ? product.nameF : product.name}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-green-50 text-green-600 hover:bg-green-500 hover:text-white transition-all duration-200 cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-sm"></i>
                      </a>
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                          addedIds.includes(product.id)
                            ? "bg-green-500 text-white"
                            : "bg-gradient-to-r from-amber-700 to-amber-500 text-white hover:scale-105"
                        }`}
                      >
                        <i className={`${addedIds.includes(product.id) ? "ri-checkbox-circle-line" : "ri-shopping-bag-line"} text-xs`}></i>
                        {addedIds.includes(product.id) ? (isFr ? "Ajouté!" : "Added!") : t("shop_add_cart")}
                      </button>
                    </div>
                    <a
                      href={FB_MARKETPLACE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-600 hover:text-white transition-all duration-200 cursor-pointer"
                      title="Facebook Marketplace"
                    >
                      <i className="ri-facebook-line text-sm"></i>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}